import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import store from "./State/Store/store";
import App from "./App";
import { initializeGame, loadIntroAssets } from "./State/actions";
import useWakeLock from "./Hooks/useWakeLock";
import "./i18n";
// import SessionManager from "./Components/UserInterface/shared/Other/SessionManager";
import HeartbeatManager from "./Components/UserInterface/shared/Other/HeartbeatManager";
import IdleLogoutManager from "./Components/UserInterface/shared/Other/IdleLogoutManager";
import { AppActivityProvider } from "./Contexts/AppActivityContext";
import { ReelControlProvider } from "./Contexts/ReelControlContext";
import { HoldAndWinControlContextProvider } from "./Contexts/HoldAndWinControlContext";
import { RoyalCollectControlContextProvider } from "./Contexts/RoyalCollectControlContext";
import { PotCollectionControlContextProvider } from "./Contexts/PotCollectionControlContext";
import { FreeSpinControlProvider } from "./Contexts/FreeSpinControlContext";
import { FreeSpinBasicControlProvider } from "./Contexts/FreeSpinBasicControlContext";
import { DimensionsProvider } from "./Contexts/DimensionsContext";
import { ReelRefsProvider } from "./Contexts/ReelRefsContext";
import { SlotRegistryProvider } from "./Contexts/SlotRegistryContext";
import HiddenAutoplayInputs from "./Components/UserInterface/shared/Other/HiddenAutoplayInputs";
import { FEATURES_CONFIG } from "./featuresConfig";
import { injectStyles } from "./utils/injectStyles";

// Inject critical layout CSS before React renders — prevents scrollbar flash
// and canvas repositioning during initialization. Font preloading still
// happens later in loadAssets (initializeStyles) since fonts aren't needed
// until the game UI mounts. The injectStyles guard (getElementById check)
// ensures this is a no-op if called again from initializeStyles.
injectStyles();

// Dispatch the initialization action
// This will load assets (including fonts) and sounds before showing the game
// Force-load just the intro popup's own assets, in parallel with the session
// call below. The intro takes over from the loader as soon as these land, so
// the remaining ~70 textures, symbol spines and sounds download while the
// player is already reading the feature slider.
store.dispatch(loadIntroAssets());
store.dispatch(initializeGame());

// Wait for DOM to be ready before initializing React
const initApp = () => {
  try {
    // Check if body exists
    if (!document.body) {
      throw new Error("document.body is not available");
    }

    // Try to find existing container
    let container = document.getElementById("game");

    // Create container if it doesn't exist
    if (!container) {
      container = document.createElement("div");
      container.id = "game";
      document.body.appendChild(container);
    }

    // Verify container is in the DOM
    if (!container.parentNode) {
      throw new Error("Failed to attach container to DOM");
    }

    // Create React root
    const root = ReactDOM.createRoot(container);

    const WakeLockBootstrap = React.memo(() => {
      useWakeLock(true);
      return null;
    });

    const FreeSpinControl = FEATURES_CONFIG.FREE_SPINS.ENABLED
      ? FreeSpinControlProvider
      : React.Fragment;
    // The Mad Labs round. Separate from FreeSpinControl above, which is Zeus's
    // mask-reveal flow and shares nothing with it but the words "free spin".
    const FreeSpinBasicControl = FEATURES_CONFIG.FREE_SPINS.ENABLED
      ? FreeSpinBasicControlProvider
      : React.Fragment;
    // Cleopatra's Hold & Win control — always mounted: its room shows only
    // when the server says H&W is live. HOLD_AND_WIN.ENABLED is Griffin's.
    // const HoldAndWinControl = FEATURES_CONFIG.HOLD_AND_WIN.ENABLED
    const HoldAndWinControl = true
      ? HoldAndWinControlContextProvider
      : React.Fragment;
    const RoyalCollectControl = FEATURES_CONFIG.ROYAL_COLLECT.ENABLED
      ? RoyalCollectControlContextProvider
      : React.Fragment;
    const PotCollectionControl = FEATURES_CONFIG.POT_COLLECTION.ENABLED
      ? PotCollectionControlContextProvider
      : React.Fragment;

    // Render the app
    root.render(
      <Provider store={store}>
        <WakeLockBootstrap />
        {/* <SessionManager /> */}

        <HeartbeatManager />
        {/* The operator's inactivity rule — inert unless __OPTIONS__ carries
            license_rules.log_out.interval. See utils/sessionTimeout. */}
        <IdleLogoutManager />
        {FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED && (
          <HiddenAutoplayInputs />
        )}

        <AppActivityProvider>
          <DimensionsProvider>
          <ReelRefsProvider>
          <SlotRegistryProvider>
            <FreeSpinControl>
              <FreeSpinBasicControl>
              <HoldAndWinControl>
                <RoyalCollectControl>
                  <PotCollectionControl>
                    <ReelControlProvider>
                      <App />
                    </ReelControlProvider>
                  </PotCollectionControl>
                </RoyalCollectControl>
              </HoldAndWinControl>
              </FreeSpinBasicControl>
            </FreeSpinControl>
          </SlotRegistryProvider>
          </ReelRefsProvider>
        </DimensionsProvider>
        </AppActivityProvider>
      </Provider>,
    );
  } catch (error) {
    throw error;
  }
};

// Ensure DOM is ready before initializing React
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initApp);
} else {
  // DOM is already ready (script loaded after DOMContentLoaded)
  initApp();
}
